import type { NoticePartnerRecord, NoticePublicRecord } from '@navio/nsdk';
import { cn } from 'noddi-ui-common';
import { formatDisplayDateTime, MISSING_VALUE } from 'noddi-util';
import {
  noticeBannerTranslations,
  type NoticeActorTranslations,
  type NoticeBannerTranslations
} from './noticeBannerTranslations';

export type NoticeBannerNotice = NoticePublicRecord | NoticePartnerRecord;

type Props = {
  notice: NoticeBannerNotice;
  /** Customer passes the time line. Partner records also name who changed the notice. */
  translations?: NoticeBannerTranslations | NoticeActorTranslations;
  className?: string;
};

const lineClassName = 'text-xs opacity-80';

const isPartnerNotice = (notice: NoticeBannerNotice): notice is NoticePartnerRecord =>
  'createdByName' in notice || 'updatedByName' in notice;

const isActorTranslations = (
  translations: NoticeBannerTranslations | NoticeActorTranslations
): translations is NoticeActorTranslations => 'createdBy' in translations;

/** Last change under the message. A partner notice also names who made it. */
export const NoticeUpdatedAt = ({ notice, translations = noticeBannerTranslations, className }: Props) => {
  if (!notice.showUpdatedAt) {
    return null;
  }

  if (isPartnerNotice(notice)) {
    const lines = actorLines(notice, isActorTranslations(translations) ? translations : noticeBannerTranslations);
    if (lines.length === 0) {
      return null;
    }

    return (
      <div className={cn('flex flex-col', className)}>
        {lines.map((line) => (
          <p key={line} className={lineClassName}>
            {line}
          </p>
        ))}
      </div>
    );
  }

  const when = formatDisplayDateTime(notice.updatedAt ?? notice.createdAt);
  if (when === MISSING_VALUE) {
    return null;
  }

  return <p className={cn(lineClassName, className)}>{translations.updatedAt(when)}</p>;
};

const actorLines = (notice: NoticePartnerRecord, translations: NoticeActorTranslations): string[] => {
  const lines: string[] = [];
  const createdWhen = formatDisplayDateTime(notice.createdAt);
  if (createdWhen !== MISSING_VALUE) {
    lines.push(
      notice.createdByName
        ? translations.createdBy(createdWhen, notice.createdByName)
        : translations.createdAt(createdWhen)
    );
  }

  if (!notice.updatedAt) {
    return lines;
  }

  const updatedWhen = formatDisplayDateTime(notice.updatedAt);
  if (updatedWhen === MISSING_VALUE) {
    return lines;
  }

  lines.push(
    notice.updatedByName
      ? translations.updatedBy(updatedWhen, notice.updatedByName)
      : translations.updatedAt(updatedWhen)
  );
  return lines;
};
